'use client';

/**
 * Route-level error boundary. Renders for any uncaught error inside a page
 * tree below the root layout. Must be a client component (Next.js requirement).
 *
 * Uses Next.js 16's `unstable_retry` to re-attempt the segment without losing
 * scroll position or other state — preferred over the older `reset` prop.
 */

import { useEffect } from 'react';
import Link from 'next/link';
import { useT } from '@/lib/i18n';

export default function Error({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  const t = useT();

  useEffect(() => {
    // Log so it surfaces in any browser-error reporting we wire up later.
    // The digest matches the server-side log entry.
    console.error('[error.tsx]', error);
  }, [error]);

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'grid',
        placeItems: 'center',
        padding: '40px 20px',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 480 }}>
        <div
          style={{
            fontSize: 48,
            marginBottom: 16,
            opacity: 0.6,
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 48 }}>
            error
          </span>
        </div>
        <h1
          style={{
            fontFamily: 'var(--font-headline)',
            fontSize: 32,
            margin: '0 0 12px',
          }}
        >
          {t('errors.server_title')}
        </h1>
        <p style={{ color: 'var(--ink-2)', marginBottom: 24, lineHeight: 1.6 }}>
          {t('errors.server_body')}
        </p>
        {error.digest && (
          <p
            style={{
              color: 'var(--ink-3)',
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
              marginBottom: 24,
            }}
          >
            Ref: {error.digest}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => unstable_retry()}
            className="ps-btn ps-btn-primary"
          >
            <span className="material-symbols-outlined ps-icon">refresh</span>
            {t('errors.try_again')}
          </button>
          <Link href="/" className="ps-btn ps-btn-secondary no-underline">
            <span className="material-symbols-outlined ps-icon">home</span>
            {t('legal.back_home')}
          </Link>
        </div>
      </div>
    </div>
  );
}
